import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
    Pane, Heading, Button, Spinner, Table, Badge, Dialog, 
    TextInputField, SelectField, IconButton, TrashIcon, EditIcon,
    toaster
} from 'evergreen-ui';
import { Formik } from 'formik';

import { 
    getShop, deleteShop, getShopEmployees, 
    addShopEmployee, updateShopEmployee, removeShopEmployee 
} from '../actions/ShopActions';
import { getUsers } from '../../users/actions/UserActions';
import InfoBlock from '../../common/InfoBlock/InfoBlock';
import Configuration from '../Config';

const DAYS_OF_WEEK = [
    { key: 'monday', label: 'Luni' },
    { key: 'tuesday', label: 'Marți' },
    { key: 'wednesday', label: 'Miercuri' },
    { key: 'thursday', label: 'Joi' },
    { key: 'friday', label: 'Vineri' },
    { key: 'saturday', label: 'Sâmbătă' },
    { key: 'sunday', label: 'Duminică' }
];

const COLOR_OPTIONS = [
    { value: '#4CAF50', label: 'Verde' },
    { value: '#2196F3', label: 'Albastru' },
    { value: '#f44336', label: 'Roșu' },
    { value: '#ff9800', label: 'Portocaliu' },
    { value: '#9c27b0', label: 'Mov' },
    { value: '#00bcd4', label: 'Cyan' },
    { value: '#E91E63', label: 'Roz' },
    { value: '#795548', label: 'Maro' }
];

class ViewShop extends Component {
    state = {
        showAddEmployeeDialog: false,
        showEditEmployeeDialog: false,
        showDeleteDialog: false,
        selectedEmployee: null
    };

    constructor(props) {
        super(props);
        this.id = this.props.params.id;
    }

    componentDidMount() {
        this.props.dispatch(getShop(this.id));
        this.props.dispatch(getShopEmployees(this.id));
        // Only load users with role level >= 2 (Staff, Managers, Admins)
        this.props.dispatch(getUsers({ start: 0, limit: 1000, extraFilters: { minRoleLevel: 2 } }));
    }

    handleDelete = () => {
        this.props.dispatch(deleteShop(this.id));
    }

    handleAddEmployee = (values) => {
        this.props.dispatch(addShopEmployee(this.id, values));
        this.setState({ showAddEmployeeDialog: false });
    }

    handleUpdateEmployee = (values) => {
        this.props.dispatch(updateShopEmployee(this.state.selectedEmployee.id, values));
        this.setState({ showEditEmployeeDialog: false, selectedEmployee: null });
    }

    handleRemoveEmployee = (employeeId) => {
        this.props.dispatch(removeShopEmployee(employeeId));
    }

    getAvailableUsers = () => {
        const assignedUserIds = this.props.employees.map(e => e.userId);
        // Filter out users that are already assigned to this shop
        // (role level filtering is done at the API level)
        return this.props.users.filter(u => !assignedUserIds.includes(u.id));
    }

    render() {
        const { current, employees, loading, users } = this.props;
        const { showAddEmployeeDialog, showEditEmployeeDialog, showDeleteDialog, selectedEmployee } = this.state;

        if (loading || !current.id) {
            return (
                <Pane display="flex" alignItems="center" justifyContent="center" height={400}>
                    <Spinner />
                </Pane>
            );
        }

        const availableUsers = this.getAvailableUsers();

        return (
            <Pane padding={16} paddingTop={0}>
                <Pane display="flex" justifyContent="space-between">
                    <Pane>
                        <Link to="/shops">
                            <Button iconBefore="arrow-left" intent="none">
                                Înapoi la lista magazinelor
                            </Button>
                        </Link>
                    </Pane>
                    <Pane>
                        <Link to={`/shops/${current.id}/edit`}>
                            <Button iconBefore="edit" marginRight={8}>
                                Editează
                            </Button>
                        </Link>
                        <Button 
                            iconBefore="trash" 
                            intent="danger"
                            onClick={() => this.setState({ showDeleteDialog: true })}
                        >
                            Șterge
                        </Button>
                    </Pane>
                </Pane>

                <Pane flex={1} background="tint1" elevation={1} padding={16} marginTop={16}>
                    <Pane display="flex" alignItems="center" gap={16}>
                        <Pane 
                            width={24} 
                            height={24} 
                            borderRadius={4} 
                            backgroundColor={current.color || '#3174ad'} 
                        />
                        <Heading size={600}>{current.name}</Heading>
                        <Badge color={current.active ? 'green' : 'neutral'}>
                            {current.active ? 'Activ' : 'Inactiv'}
                        </Badge>
                    </Pane>
                </Pane>

                <Pane display="flex" gap={16} marginTop={16}>
                    {/* Shop Details */}
                    <InfoBlock flex="1" title="Detalii Magazin">
                        <Table>
                            <Table.Body>
                                <Table.Row>
                                    <Table.TextCell><strong>Adresă</strong></Table.TextCell>
                                    <Table.TextCell>{current.address || '-'}</Table.TextCell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.TextCell><strong>Telefon</strong></Table.TextCell>
                                    <Table.TextCell>{current.phone || '-'}</Table.TextCell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.TextCell><strong>Email</strong></Table.TextCell>
                                    <Table.TextCell>{current.email || '-'}</Table.TextCell>
                                </Table.Row>
                                <Table.Row>
                                    <Table.TextCell><strong>Personal Minim/Tură</strong></Table.TextCell>
                                    <Table.TextCell>{current.minStaffPerShift}</Table.TextCell>
                                </Table.Row>
                            </Table.Body>
                        </Table>
                    </InfoBlock>

                    {/* Operating Hours */}
                    <InfoBlock flex="1" title="Program de Funcționare">
                        <Table>
                            <Table.Body>
                                {DAYS_OF_WEEK.map(day => {
                                    const hours = current.operatingHours?.[day.key];
                                    return (
                                        <Table.Row key={day.key}>
                                            <Table.TextCell><strong>{day.label}</strong></Table.TextCell>
                                            <Table.TextCell>
                                                {hours?.closed 
                                                    ? <Badge color="neutral">Închis</Badge>
                                                    : `${hours?.open || '09:00'} - ${hours?.close || '21:00'}`
                                                }
                                            </Table.TextCell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </InfoBlock>
                </Pane>

                {/* Employees Section */}
                <Pane marginTop={24}>
                    <Pane display="flex" justifyContent="space-between" alignItems="center" marginBottom={16}>
                        <Heading size={500}>Angajați ({employees.length})</Heading>
                        <Button 
                            iconBefore="add" 
                            appearance="primary"
                            onClick={() => this.setState({ showAddEmployeeDialog: true })}
                            disabled={availableUsers.length === 0}
                        >
                            Adaugă Angajat
                        </Button>
                    </Pane>

                    <Table>
                        <Table.Head>
                            <Table.TextHeaderCell>Culoare</Table.TextHeaderCell>
                            <Table.TextHeaderCell>Nume</Table.TextHeaderCell>
                            <Table.TextHeaderCell>Email</Table.TextHeaderCell>
                            <Table.TextHeaderCell>Rol Utilizator</Table.TextHeaderCell>
                            <Table.TextHeaderCell>Ore/Săptămână</Table.TextHeaderCell>
                            <Table.TextHeaderCell>Status</Table.TextHeaderCell>
                            <Table.TextHeaderCell>Acțiuni</Table.TextHeaderCell>
                        </Table.Head>
                        <Table.Body>
                            {employees.map(emp => (
                                <Table.Row key={emp.id}>
                                    <Table.Cell>
                                        <Pane 
                                            width={20} 
                                            height={20} 
                                            borderRadius={4} 
                                            backgroundColor={emp.color || '#4CAF50'} 
                                        />
                                    </Table.Cell>
                                    <Table.TextCell>{emp.user?.name || '-'}</Table.TextCell>
                                    <Table.TextCell>{emp.user?.email || '-'}</Table.TextCell>
                                    <Table.TextCell>
                                        <Badge color={emp.user?.role?.level === 3 ? 'red' : emp.user?.role?.level === 2 ? 'orange' : 'green'}>
                                            {emp.user?.role?.name || '-'}
                                        </Badge>
                                    </Table.TextCell>
                                    <Table.TextCell>{emp.contractHoursPerWeek}h</Table.TextCell>
                                    <Table.TextCell>
                                        <Badge color={emp.active ? 'green' : 'neutral'}>
                                            {emp.active ? 'Activ' : 'Inactiv'}
                                        </Badge>
                                    </Table.TextCell>
                                    <Table.Cell>
                                        <IconButton 
                                            icon={EditIcon} 
                                            size="small" 
                                            marginRight={8}
                                            onClick={() => this.setState({ 
                                                showEditEmployeeDialog: true, 
                                                selectedEmployee: emp 
                                            })}
                                        />
                                        <IconButton 
                                            icon={TrashIcon} 
                                            size="small" 
                                            intent="danger"
                                            onClick={() => this.handleRemoveEmployee(emp.id)}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                            {employees.length === 0 && (
                                <Table.Row>
                                    <Table.TextCell colSpan={7} textAlign="center">
                                        Nu există angajați asignați acestui magazin
                                    </Table.TextCell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Pane>

                {/* Add Employee Dialog */}
                <Dialog
                    isShown={showAddEmployeeDialog}
                    title="Adaugă Angajat"
                    onCloseComplete={() => this.setState({ showAddEmployeeDialog: false })}
                    hasFooter={false}
                >
                    <Formik
                        initialValues={Configuration.Defaults.addEmployee}
                        validationSchema={Configuration.Validations.employee}
                        onSubmit={this.handleAddEmployee}
                    >
                        {({ values, errors, handleChange, handleSubmit, setFieldValue }) => (
                            <form onSubmit={handleSubmit}>
                                <SelectField
                                    label="Utilizator"
                                    name="userId"
                                    required
                                    value={values.userId || ''}
                                    onChange={(e) => setFieldValue('userId', parseInt(e.target.value))}
                                    validationMessage={errors.userId}
                                >
                                    <option value="">Selectează utilizator...</option>
                                    {availableUsers.map(user => (
                                        <option key={user.id} value={user.id}>
                                            {user.name} ({user.email}) - {user.role?.name || 'N/A'}
                                        </option>
                                    ))}
                                </SelectField>

                                <TextInputField
                                    label="Ore Contract/Săptămână"
                                    name="contractHoursPerWeek"
                                    type="number"
                                    min={1}
                                    max={168}
                                    value={values.contractHoursPerWeek}
                                    onChange={handleChange}
                                    validationMessage={errors.contractHoursPerWeek}
                                />

                                <SelectField
                                    label="Culoare Calendar"
                                    name="color"
                                    value={values.color}
                                    onChange={handleChange}
                                >
                                    {COLOR_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </SelectField>

                                <Pane display="flex" justifyContent="flex-end" marginTop={16}>
                                    <Button 
                                        marginRight={8}
                                        onClick={() => this.setState({ showAddEmployeeDialog: false })}
                                    >
                                        Anulează
                                    </Button>
                                    <Button type="submit" appearance="primary">
                                        Adaugă
                                    </Button>
                                </Pane>
                            </form>
                        )}
                    </Formik>
                </Dialog>

                {/* Edit Employee Dialog */}
                <Dialog
                    isShown={showEditEmployeeDialog}
                    title="Editează Angajat"
                    onCloseComplete={() => this.setState({ showEditEmployeeDialog: false, selectedEmployee: null })}
                    hasFooter={false}
                >
                    {selectedEmployee && (
                        <Formik
                            initialValues={{
                                role: selectedEmployee.role,
                                contractHoursPerWeek: selectedEmployee.contractHoursPerWeek,
                                color: selectedEmployee.color,
                                active: selectedEmployee.active
                            }}
                            onSubmit={this.handleUpdateEmployee}
                        >
                            {({ values, handleChange, handleSubmit, setFieldValue }) => (
                                <form onSubmit={handleSubmit}>
                                    <Pane marginBottom={16}>
                                        <strong>Angajat:</strong> {selectedEmployee.user?.name}
                                    </Pane>

                                    <TextInputField
                                        label="Ore Contract/Săptămână"
                                        name="contractHoursPerWeek"
                                        type="number"
                                        min={1}
                                        max={168}
                                        value={values.contractHoursPerWeek}
                                        onChange={handleChange}
                                    />

                                    <SelectField
                                        label="Culoare Calendar"
                                        name="color"
                                        value={values.color}
                                        onChange={handleChange}
                                    >
                                        {COLOR_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </SelectField>

                                    <Pane display="flex" justifyContent="flex-end" marginTop={16}>
                                        <Button 
                                            marginRight={8}
                                            onClick={() => this.setState({ showEditEmployeeDialog: false, selectedEmployee: null })}
                                        >
                                            Anulează
                                        </Button>
                                        <Button type="submit" appearance="primary">
                                            Salvează
                                        </Button>
                                    </Pane>
                                </form>
                            )}
                        </Formik>
                    )}
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    isShown={showDeleteDialog}
                    title="Confirmare ștergere"
                    intent="danger"
                    onCloseComplete={() => this.setState({ showDeleteDialog: false })}
                    confirmLabel="Șterge"
                    onConfirm={this.handleDelete}
                >
                    Ești sigur că vrei să ștergi magazinul "{current.name}"?
                </Dialog>
            </Pane>
        );
    }
}

function mapStateToProps(state) {
    return {
        current: state.shops.current,
        employees: state.shops.employees,
        users: state.users.data,
        loading: state.loading.active
    };
}

export default connect(mapStateToProps)(ViewShop);
